import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/product/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Search, X, ChevronDown, ChevronUp, SlidersHorizontal, 
  Package, Star, Check, Grid3X3, LayoutGrid
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { searchProducts } from '@/lib/searchUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState(() => {
    const b = searchParams.get('brand');
    return b ? [b] : [];
  });
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [maxPrice, setMaxPrice] = useState(500000);
  const [sortBy, setSortBy] = useState('default');
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    brand: true,
    price: true,
    rating: false
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [gridCols, setGridCols] = useState(4);

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') || 'all';
    const brandFromUrl = searchParams.get('brand');
    if (categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
      setSelectedBrands(brandFromUrl ? [brandFromUrl] : []);
    } else if (brandFromUrl && !selectedBrands.includes(brandFromUrl)) {
      setSelectedBrands([brandFromUrl]);
    }
  }, [searchParams, selectedCategory, selectedBrands]);

  // Sync search term from URL (e.g. header search navigates to /products?search=...)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl !== null) {
      setSearchTerm(searchFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories([{ id: 'all', name: 'All Products' }, ...response.data.categories]);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        const brandsList = response.data.brands_list || [];
        if (selectedCategory === 'all') {
          const uniqueBrands = [...new Set(brandsList.map(b => b.name))];
          setBrands(uniqueBrands);
        } else {
          const categoryBrands = brandsList
            .filter(b => b.category === selectedCategory)
            .map(b => b.name);
          setBrands([...new Set(categoryBrands)]);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch brands:', error);
      }
    };
    fetchBrands();
  }, [selectedCategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = { limit: 1000 };
        if (selectedCategory !== 'all') params.category = selectedCategory;
        const response = await axios.get(`${API}/products`, { params });
        setAllProducts(response.data);
        
        if (response.data.length > 0) {
          const max = Math.max(...response.data.map(p => p.price));
          setMaxPrice(max);
          setPriceRange([0, max]);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    if (searchTerm) {
      filtered = searchProducts(filtered, searchTerm);
    }

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    if (minRating > 0) {
      filtered = filtered.filter(p => (p.avg_rating || 0) >= minRating);
    }

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      default:
        break;
    }

    return filtered;
  }, [allProducts, searchTerm, selectedBrands, priceRange, sortBy, minRating]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedBrands([]);
    setPriceRange([0, maxPrice]);
    setSortBy('default');
    setSearchTerm('');
    setMinRating(0);
    setSearchParams({});
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedBrands([]);
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
    setMobileFiltersOpen(false);
  };

  const activeFiltersCount = (selectedCategory !== 'all' ? 1 : 0) + 
    selectedBrands.length + 
    (priceRange[0] > 0 || priceRange[1] < maxPrice ? 1 : 0) +
    (minRating > 0 ? 1 : 0);

  const FilterSidebar = ({ isMobile = false }) => (
    <div className={`space-y-5 ${isMobile ? '' : 'sticky top-24'}`}>
      {activeFiltersCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="text-red-500 hover:text-red-600 hover:bg-red-50 w-full justify-start"
        >
          <X className="w-4 h-4 mr-2" />
          Clear All ({activeFiltersCount})
        </Button>
      )}

      {/* Categories */}
      {!isMobile && (
        <div className="pb-4 border-b border-slate-100">
          <button
            onClick={() => toggleSection('category')}
            className="flex items-center justify-between w-full text-left font-semibold text-slate-900 mb-3"
          >
            <span>Categories</span>
            {expandedSections.category ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <AnimatePresence>
            {expandedSections.category && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-1 overflow-hidden"
              >
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                      selectedCategory === category.id
                        ? 'bg-[#0047AB] text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {category.name}
                    {selectedCategory === category.id && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Brands */}
      {brands.length > 0 && (
        <div className="pb-4 border-b border-slate-100">
          <button
            onClick={() => toggleSection('brand')}
            className="flex items-center justify-between w-full text-left font-semibold text-slate-900 mb-3"
          >
            <span>Brands</span>
            {expandedSections.brand ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <AnimatePresence>
            {expandedSections.brand && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-1 overflow-hidden max-h-48 overflow-y-auto"
              >
                {brands.map((brand) => (
                  <label
                    key={brand}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand)}
                      onChange={() => toggleBrand(brand)}
                      className="w-4 h-4 rounded border-slate-300 text-[#0047AB] focus:ring-[#0047AB]"
                    />
                    <span className="text-sm text-slate-600">{brand}</span>
                  </label>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Price Range */}
      <div className="pb-4 border-b border-slate-100">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full text-left font-semibold text-slate-900 mb-3"
        >
          <span>Price Range</span>
          {expandedSections.price ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <AnimatePresence>
          {expandedSections.price && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-4 overflow-hidden"
            >
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={maxPrice}
                  step={100}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={priceRange[0]}
                  onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                  className="h-9 text-sm"
                  placeholder="₹0"
                />
                <span className="text-slate-400">—</span>
                <Input
                  type="number"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                  className="h-9 text-sm"
                  placeholder={`₹${maxPrice}`}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Rating */}
      <div className="pb-4">
        <button
          onClick={() => toggleSection('rating')}
          className="flex items-center justify-between w-full text-left font-semibold text-slate-900 mb-3"
        >
          <span>Rating</span>
          {expandedSections.rating ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <AnimatePresence>
          {expandedSections.rating && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 overflow-hidden"
            >
              {[4, 3, 2, 1].map((rating) => (
                <button
                  key={rating}
                  onClick={() => setMinRating(minRating === rating ? 0 : rating)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    minRating === rating ? 'bg-amber-50 text-amber-700' : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={`star-${rating}-${i}`}
                        className={`w-3.5 h-3.5 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                      />
                    ))}
                  </div>
                  <span>& Up</span>
                  {minRating === rating && <Check className="w-4 h-4 ml-auto" />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section - Clean like papita.ae */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-outfit font-bold text-slate-900">
                {selectedCategory === 'all' ? 'All Products' : categories.find(c => c.id === selectedCategory)?.name || 'Products'}
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                {loading ? 'Loading...' : `${filteredProducts.length} products found`}
              </p>
            </div>
            
            {/* Category Pills */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-[#0047AB] text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                  data-testid={`category-pill-${category.id}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Sort & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 px-4 border border-slate-200 rounded-lg text-sm bg-white focus:border-[#0047AB] focus:ring-[#0047AB] min-w-[160px]"
            data-testid="sort-select"
          >
            <option value="default">Sort: Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Top Rated</option>
            <option value="newest">Newest First</option>
          </select>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              className="pl-10 h-10 bg-white border-slate-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Grid Toggle - Desktop */}
          <div className="hidden md:flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setGridCols(4)}
              className={`p-2 rounded ${gridCols === 4 ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            >
              <Grid3X3 className="w-4 h-4 text-slate-600" />
            </button>
            <button
              onClick={() => setGridCols(3)}
              className={`p-2 rounded ${gridCols === 3 ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            >
              <LayoutGrid className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden h-10 px-4 border-slate-200">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-2 bg-[#0047AB] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0">
              <SheetHeader className="px-6 py-4 border-b border-slate-100">
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="px-6 py-4 overflow-y-auto max-h-[calc(100vh-140px)]">
                <FilterSidebar isMobile={true} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100">
                <SheetClose asChild>
                  <Button className="w-full bg-[#0047AB] hover:bg-[#003380]">
                    Show Results ({filteredProducts.length})
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <div className="bg-white rounded-xl p-5 border border-slate-100">
              <h2 className="font-semibold text-slate-900 mb-4">Filters</h2>
              <FilterSidebar />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="text-xs text-slate-500">Active:</span>
                {selectedCategory !== 'all' && (
                  <span className="inline-flex items-center gap-1 bg-blue-50 text-[#0047AB] px-3 py-1 rounded-full text-xs font-medium">
                    {categories.find(c => c.id === selectedCategory)?.name}
                    <button onClick={() => handleCategoryChange('all')}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedBrands.map(brand => (
                  <span key={brand} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-medium">
                    {brand}
                    <button onClick={() => toggleBrand(brand)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {minRating > 0 && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-medium">
                    {minRating}+ Stars
                    <button onClick={() => setMinRating(0)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-xs text-red-500 hover:text-red-600 font-medium"
                >
                  Clear all
                </button>
              </div>
            )}

            {loading ? (
              <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-${gridCols} gap-4`}>
                {[...Array(8)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="bg-white rounded-lg overflow-hidden border border-slate-100 animate-pulse">
                    <div className="aspect-square bg-slate-100" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-slate-100 rounded w-1/3" />
                      <div className="h-4 bg-slate-100 rounded w-3/4" />
                      <div className="h-4 bg-slate-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16 bg-white rounded-xl border border-slate-100"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No products found</h3>
                <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                  Try adjusting your filters or search term.
                </p>
                <Button onClick={clearFilters} className="bg-[#0047AB] hover:bg-[#003380]">
                  Clear Filters
                </Button>
              </motion.div>
            ) : (
              <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-${gridCols} gap-4`}>
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
